import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Stack,
    CircularProgress,
    FormControlLabel,
    Switch,
    Grid,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { createRole, updateRole, getRoleById } from '../Services/RolesPermissions/RolesPermissionsApi';
import { useGlobalSnackbar } from "../Components/Common/GlobalSanckbar";

const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

const AddEditRolesPermissions = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams(); // For edit mode
    const isEdit = Boolean(id);
    const editStateRole = location.state?.role;
    const { showSnackbar } = useGlobalSnackbar();

    const [name, setName] = useState(editStateRole?.name || "");
    const [description, setDescription] = useState(editStateRole?.description || "");
    const [isActive, setIsActive] = useState(editStateRole?.isActive ?? true);
    const [auditInfo, setAuditInfo] = useState({
        cuName: editStateRole?.cuName || "",
        cuType: editStateRole?.cuType || "",
        cuId: editStateRole?.cuId || "",
        uuName: editStateRole?.uuName || "",
        uuType: editStateRole?.uuType || "",
        uuId: editStateRole?.uuId || "",
        createdAt: editStateRole?.createdAt || "",
        updatedAt: editStateRole?.updatedAt || ""
    });

    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (isEdit) {
            const fetchRole = async () => {
                try {
                    if (!editStateRole) {
                        setLoading(true);
                    }
                    const response = await getRoleById(id);
                    if (response && response.success && response.data) {
                        let data = response.data;
                        if (Array.isArray(data)) {
                            data = data.find(item => item.id?.toString() === id.toString()) || data[0];
                        }
                        if (data) {
                            setName(data.name || "");
                            setDescription(data.description || "");
                            setIsActive(data.isActive ?? true);
                            setAuditInfo({
                                cuName: data.cuName || "",
                                cuType: data.cuType || "",
                                cuId: data.cuId || "",
                                uuName: data.uuName || "",
                                uuType: data.uuType || "",
                                uuId: data.uuId || "",
                                createdAt: data.createdAt || "",
                                updatedAt: data.updatedAt || ""
                            });
                        }
                    }
                } catch (err) {
                    console.error("Failed to fetch role", err);
                    showSnackbar("Failed to load role data", "error");
                } finally {
                    setLoading(false);
                }
            };
            fetchRole();
        }
    }, [id, isEdit, editStateRole, showSnackbar]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFieldErrors({});

        const roleData = {
            name,
            description,
            ...(isEdit && { isActive }),
        };

        try {
            if (isEdit) {
                const response = await updateRole(id, roleData);
                showSnackbar(response?.message || "Role updated successfully!", "success");
            } else {
                const response = await createRole(roleData);
                showSnackbar(response?.message || "Role created successfully!", "success");
            }
            setTimeout(() => {
                navigate("/roles-permissions");
            }, 1500);
        } catch (err) {
            console.error("Failed to save role", err);
            const serverError = err.response?.data;
            const errMsg = serverError?.errors?.[0] ||
                serverError?.details?.[0]?.message ||
                serverError?.message ||
                err.message ||
                "Failed to save role. Please try again.";

            showSnackbar(errMsg, "error");

            const errorsObj = {};
            if (serverError && serverError.details && Array.isArray(serverError.details)) {
                serverError.details.forEach(item => {
                    const fieldName = item.field === 'general' ? 'name' : item.field;
                    errorsObj[fieldName] = item.message;
                });
            } else if (serverError && serverError.errors && Array.isArray(serverError.errors) && serverError.errors.length > 0) {
                errorsObj['name'] = serverError.errors[0];
            } else {
                errorsObj['name'] = errMsg;
            }
            setFieldErrors(errorsObj);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate("/roles-permissions");
    };

    return (
        <Box sx={{ p: 3 }}>


            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    {isEdit ? "Edit Role" : "Add Role"}
                </Typography>
            </Box>

            {/* Alerts handled by GlobalSnackbar */}

            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            label="Role Name *"
                            value={name} size="small"
                            onChange={(e) => {
                                const val = e.target.value;
                                const sanitized = val.replace(/[^a-zA-Z\s]/g, '');
                                setName(sanitized);
                                if (fieldErrors.name) {
                                    setFieldErrors(prev => ({ ...prev, name: '' }));
                                }
                            }}
                            error={Boolean(fieldErrors.name)}
                            helperText={fieldErrors.name}
                            variant="outlined"
                        />

                        <TextField
                            fullWidth
                            label="Description"
                            value={description} size="small"
                            onChange={(e) => {
                                setDescription(e.target.value);
                                if (fieldErrors.description) {
                                    setFieldErrors(prev => ({ ...prev, description: '' }));
                                }
                            }}
                            error={Boolean(fieldErrors.description)}
                            helperText={fieldErrors.description}
                            multiline
                            rows={4}
                            variant="outlined"
                        />

                        {isEdit && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Active"
                            />
                        )}

                        {isEdit && (
                            <Box sx={{ mt: 1, pt: 2.5, borderTop: '1px solid #EAEAEA' }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: '#475569',
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    Audit Information
                                    <Typography component="span" sx={{ fontSize: '0.75rem', color: '#94A3B8', fontWeight: 400 }}>
                                        (Non-editable)
                                    </Typography>
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Created By"
                                            value={auditInfo.cuName ? `${auditInfo.cuName}${auditInfo.cuType ? ` (${auditInfo.cuType})` : ''}` : '-'}
                                            size="small"
                                            disabled
                                            variant="outlined"
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: '#475569',
                                                },
                                                bgcolor: '#F8FAFC'
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Created At"
                                            value={formatDateTime(auditInfo.createdAt)}
                                            size="small"
                                            disabled
                                            variant="outlined"
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: '#475569',
                                                },
                                                bgcolor: '#F8FAFC'
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Updated By"
                                            value={auditInfo.uuName ? `${auditInfo.uuName}${auditInfo.uuType ? ` (${auditInfo.uuType})` : ''}` : '-'}
                                            size="small"
                                            disabled
                                            variant="outlined"
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: '#475569',
                                                },
                                                bgcolor: '#F8FAFC'
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Updated At"
                                            value={formatDateTime(auditInfo.updatedAt)}
                                            size="small"
                                            disabled
                                            variant="outlined"
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: '#475569',
                                                },
                                                bgcolor: '#F8FAFC'
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                onClick={handleCancel}
                                disabled={loading}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: 3
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    bgcolor: '#FFBD65', color: '#fff',
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: 3
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    isEdit ? "Update Role" : "Create Role"
                                )}
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </Paper>
        </Box>
    );
};

export default AddEditRolesPermissions;
